import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { userLookup, sendJoinInvite } from "../../Services/Realtionship";
import ConfirmInviteModal from "../ConfirmInviteModal";
import ContentLoader from "../../Pages/LoadingPage/ContentLoader";
import { ReactComponent as MemberIcon } from "../../Assets/icon/Vector.svg"; // Aris Member
import { ReactComponent as NotMemberIcon } from "../../Assets/icon/UserCircleCross.svg"; // Not a member
import { ReactComponent as EyeIcon } from "../../Assets/images/eye.svg"; // Not a member
import "./style.css";

const isValidEmail = (v) => /[^@\s]+@[^@\s]+\.[^@\s]+/.test(String(v || "").trim());

export default function ShareElementsModal({ open, contact, onClose, onConfirmed }) {
  const [notifyVia, setNotifyVia] = useState("email");
  const [email, setEmail] = useState(contact?.contact?.email || contact?.email || "");
  const [phone, setPhone] = useState(() => {
    const cc = contact?.contact?.mobile?.country_code || contact?.country_code;
    const local = contact?.contact?.mobile?.number || contact?.contact;
    return cc && local ? `${cc}${local}` : "";
  });
  const [checking, setChecking] = useState(false);
  const [memberStatus, setMemberStatus] = useState(null); // null | 'member' | 'not'
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    // When opening (or switching contact), hydrate from contact and reset flags
    const nextEmail = contact?.contact?.email || contact?.email || "";
    const cc = contact?.contact?.mobile?.country_code || contact?.country_code;
    const local = contact?.contact?.mobile?.number || contact?.contact;
    const nextPhone = cc && local ? `${cc}${local}` : "";
    setEmail(nextEmail);
    setPhone(nextPhone);
    setMemberStatus(null);
    setChecking(false);
    setErrorMsg("");
    if (nextEmail) setNotifyVia("email");
    else if (nextPhone) setNotifyVia("phone");
    else setNotifyVia("email");
  }, [open, contact]);

  const canVerify = useMemo(() => {
    if (notifyVia === "email") return isValidEmail(email);
    const parsed = phone ? parsePhoneNumberFromString(phone) : null;
    return Boolean(parsed && parsed.isValid());
  }, [notifyVia, email, phone]);

  const memberBadge = (
    <div className={`verify-badge ${memberStatus === "member" ? "ok" : memberStatus === "not" ? "not" : "idle"}`}>
      {memberStatus === "member" ? (
        <><MemberIcon /> <span>Aris Member</span></>
      ) : memberStatus === "not" ? (
        <><NotMemberIcon /> <span>Not an Aris Member</span></>
      ) : (
        <><EyeIcon /> <span>Aris Member? Verify</span></>
      )}
    </div>
  );

  const handleVerify = async () => {
    if (!canVerify) return;
    setChecking(true);
    try {
      let exists = false;
      if (notifyVia === "email") {
        const res = await userLookup({ email: email.trim() });
        exists = !!res?.exists;
      } else {
        const parsed = phone ? parsePhoneNumberFromString(phone) : null;
        const cc = parsed ? `+${parsed.countryCallingCode}` : null;
        const local = parsed ? parsed.nationalNumber : null;
        const res = await userLookup({ country_code: cc, contact: local });
        exists = !!res?.exists;
      }
      setMemberStatus(exists ? "member" : "not");
    } finally {
      setChecking(false);
    }
  };

  const handleConfirm = async () => {
    // Determine channel and values
    const parsed = phone ? parsePhoneNumberFromString(phone) : null;
    const isEmail = notifyVia === "email" && !!email && isValidEmail(email);
    const isPhone = notifyVia === "phone" && !!parsed && parsed.isValid();

    // Only show confirm invite when we have verified that the user is not a member
    if (memberStatus === "not") {
      if (!isEmail && !isPhone) {
        setErrorMsg("Please enter a valid email or phone number.");
        return;
      }
      setShowInviteConfirm(true);
      return;
    }

    // If member, proceed to send invite (share request) with valid contact
    if (memberStatus === "member" && (isEmail || isPhone)) {
      setIsConfirming(true);
      try {
        await sendJoinInvite({
          relation_id: contact._id,
          email: isEmail ? email : undefined,
          country_code: isPhone ? `+${parsed.countryCallingCode}` : undefined,
          contact: isPhone ? String(parsed.nationalNumber) : undefined,
        });
        onConfirmed?.({ relation_id: contact._id, payload: { email: isEmail ? email : undefined, country_code: isPhone ? `+${parsed.countryCallingCode}` : undefined, contact: isPhone ? String(parsed.nationalNumber) : undefined } });
        onClose?.();
      } catch (e) {
        const apiMsg = e?.response?.data?.message || e?.message || "Failed to send invite";
        setErrorMsg(apiMsg);
      } finally {
        setIsConfirming(false);
      }
      return;
    }

    // Otherwise require verification first
    setErrorMsg("Please enter a valid email or phone and click Verify to continue.");
  };

  if (!open) return null;

  return (
    <div className="share-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-header-row">
          <span className="share-title-modal">Connect</span>
          <button className="icon-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="share-contact-card-modal">
          <div className="avatar-round">{(contact?.first_name?.[0] || "").toUpperCase()}</div>
          <div className="share-contact-info">
            <div className="name">{`${contact?.first_name || ""} ${contact?.last_name || ""}`.trim()}</div>
            <div className="sub">{contact?.related_data?.relationship_type || contact?.relationship_type || ""}</div>
          </div>
        </div>

        <div className="share-card-modal">
          <div className="share-header-modal">
            <div className="share-title-modal">Connect</div>
            <div className="share-sub">Request permission to mutually share insights for deeper relationship understanding.</div>
          </div>
          <div className="share-body">
            <div className="notify-row-share">
              <div className="notify-label">
                <span>Notify Via</span>
              </div>
              <div className="notify-radio">
                <label><input type="radio" checked={notifyVia === "email"} onChange={() => { setNotifyVia("email"); setMemberStatus(null); }} /> Email</label>
                <label><input type="radio" checked={notifyVia === "phone"} onChange={() => { setNotifyVia("phone"); setMemberStatus(null); }} /> Mobile Number</label>
              </div>
            </div>
          </div>
          <div className="share-field">
            {notifyVia === "email" ? (
              <>
                <div className="share-field-header">
                  <label className="share-input-label"></label>
                  {memberStatus === null ? (
                    <button
                      type="button"
                      className={`verify-link mobile-only ${(!canVerify || checking) ? 'disabled' : ''}`}
                      onClick={(!canVerify || checking) ? undefined : handleVerify}
                    >
                      {checking ? "Checking..." : <>{memberBadge}</>}
                    </button>
                  ) : (
                    <div className="verify-result mobile-only">{memberBadge}</div>
                  )}
                </div>
                <div className="verify-field">
                  <input
                    className="verify-input"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setMemberStatus(null); }}
                    placeholder="Email"
                  />
                  {memberStatus === null ? (
                    <button
                      className="verify-btn inset desktop-only"
                      disabled={!canVerify || checking}
                      onClick={handleVerify}
                      type="button"
                    >
                      {checking ? "Checking..." : memberBadge}
                    </button>
                  ) : (
                    <div className="verify-result desktop-only">{memberBadge}</div>
                  )}
                </div>
                {errorMsg === "Please enter a valid email or phone and click Verify to continue." ? (
                  <div className="share-inline-error" role="alert" aria-live="assertive">{errorMsg}</div>
                ) : null}
              </>
            ) : (
              <>
                <div className="share-field-header">
                  <label className="share-input-label"></label>
                  {memberStatus === null ? (
                    <button
                      type="button"
                      className={`verify-link mobile-only ${(!canVerify || checking) ? 'disabled' : ''}`}
                      onClick={(!canVerify || checking) ? undefined : handleVerify}
                    >
                      {checking ? "Checking..." : <>{memberBadge}</>}
                    </button>
                  ) : (
                    <div className="verify-result mobile-only">{memberBadge}</div>
                  )}
                </div>
                <div className="verify-field">
                  <PhoneInput
                    international
                    defaultCountry="US"
                    value={phone}
                    onChange={(v) => { setPhone(v || ""); setMemberStatus(null); }}
                    placeholder="Mobile Number"
                    className="share-phone-input"
                  />
                  {memberStatus === null ? (
                    <button
                      className="verify-btn inset desktop-only"
                      disabled={!canVerify || checking}
                      onClick={handleVerify}
                      type="button"
                    >
                      {checking ? "Checking..." : memberBadge}
                    </button>
                  ) : (
                    <div className="verify-result desktop-only">{memberBadge}</div>
                  )}
                </div>
                {errorMsg === "Please enter a valid email or phone and click Verify to continue." ? (
                  <div className="share-inline-error" role="alert" aria-live="assertive">{errorMsg}</div>
                ) : null}
              </>
            )}
          </div>

        </div>

        <div className="share-actions">
          <button className="primary" onClick={handleConfirm}>Confirm</button>
          <button className="ghost" onClick={onClose}>Cancel</button>
        </div>

        {errorMsg && errorMsg !== "Please enter a valid email or phone and click Verify to continue." ? (
          <div className="share-error" role="alert" aria-live="assertive">{errorMsg}</div>
        ) : null}

        {showInviteConfirm && (
          <ConfirmInviteModal
            open={showInviteConfirm}
            onClose={() => setShowInviteConfirm(false)}
            contactName={`${contact?.first_name || ""} ${contact?.last_name || ""}`.trim()}
            category={contact?.related_data?.relationship_category || contact?.relationship_category}
            emailOrPhone={notifyVia === "email" ? email : phone}
            onConfirm={async () => {
              // Send invite for non-member using the same API as member flow
              setIsConfirming(true);
              try {
                const parsed = phone ? parsePhoneNumberFromString(phone) : null;
                const isEmail = notifyVia === "email" && !!email && isValidEmail(email);
                const isPhone = notifyVia === "phone" && !!parsed && parsed.isValid();
                await sendJoinInvite({
                  relation_id: contact._id,
                  email: isEmail ? email : undefined,
                  country_code: isPhone ? `+${parsed.countryCallingCode}` : undefined,
                  contact: isPhone ? String(parsed.nationalNumber) : undefined,
                });
                setShowInviteConfirm(false);
                onConfirmed?.({ relation_id: contact._id });
                onClose?.();
              } catch (e) {
                const apiMsg = e?.response?.data?.message || e?.message || "Failed to send invite";
                setErrorMsg(apiMsg);
                setShowInviteConfirm(false);
                // keep confirm dialog open so user can retry or cancel
              } finally {
                setIsConfirming(false);
              }
            }}
          />
        )}

        {isConfirming && (
          <div className="share-overlay" style={{ zIndex: 1000 }}>
            <ContentLoader />
          </div>
        )}
      </div>
    </div>
  );
}
